import React, { useRef, useState } from 'react';
import { Modal, Stack, Text, Button, Group, Loader } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useGenerateMapDraft, useSaveMapDraft } from '../../hooks/useCompetencyApi';
import { DraftRows, type DraftRow } from './DraftRows';
import { getErrorMessage } from '../../utils/api';
import { showToast } from '../../utils/toast';

interface Column {
  _id: string;
  title: string;
}

interface MapDraftModalProps {
  opened: boolean;
  onClose: () => void;
  mapId: string;
  grades: Column[];
  swimlanes: Column[];
}

/**
 * ИИ-черновик карты (бета `mapAiDraft`): ИИ предлагает компетенции по грейдам карты, админ
 * правит название, описание, грейд и свимлейн, снимает лишнее и добавляет оставленное в карту.
 * До «Добавить в карту» ничего не сохраняется.
 */
export const MapDraftModal: React.FC<MapDraftModalProps> = ({
  opened,
  onClose,
  mapId,
  grades,
  swimlanes,
}) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<DraftRow[] | null>(null);
  const generate = useGenerateMapDraft(mapId);
  const save = useSaveMapDraft(mapId);

  const gradeOptions = grades.map((g) => ({ value: g._id, label: g.title }));
  const swimlaneOptions = swimlanes.map((s) => ({ value: s._id, label: s.title }));
  const kept = (rows ?? []).filter((r) => r.isKept && r.title.trim() && r.description.trim());

  // Окно закрыли, пока ИИ думал, — ответ уже не нужен: иначе он всплыл бы при следующем открытии.
  const runRef = useRef(0);

  const handleGenerate = async () => {
    const run = runRef.current;
    const data = await generate.mutateAsync().catch(() => null);
    if (data && run === runRef.current)
      setRows(data.competencies.map((c) => ({ ...c, isKept: true })));
  };

  const handleClose = () => {
    runRef.current++;
    setRows(null);
    generate.reset();
    save.reset();
    onClose();
  };

  const handleSave = async () => {
    const result = await save
      .mutateAsync(kept.map(({ isKept, ...item }) => ({ ...item, title: item.title.trim() })))
      .catch(() => null);
    if (!result) return;
    showToast({
      title: t('competencyMaps.draft.savedTitle'),
      message: t('competencyMaps.draft.saved', { count: result.created }),
      color: 'teal',
    });
    handleClose();
  };

  const error = generate.error ?? save.error;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t('competencyMaps.draft.modalTitle')}
      size="xl"
    >
      <Stack gap="md">
        {!rows && (
          <>
            <Text size="sm" c="dimmed">
              {t('competencyMaps.draft.intro')}
            </Text>
            {generate.isPending ? (
              <Group gap="sm">
                <Loader size="sm" />
                <Text size="sm">{t('competencyMaps.draft.waiting')}</Text>
              </Group>
            ) : (
              <Group>
                <Button onClick={handleGenerate}>{t('competencyMaps.draft.generate')}</Button>
              </Group>
            )}
          </>
        )}

        {rows && rows.length === 0 && <Text size="sm">{t('competencyMaps.draft.empty')}</Text>}

        {rows && rows.length > 0 && (
          <DraftRows
            rows={rows}
            onChange={setRows}
            grades={gradeOptions}
            swimlanes={swimlaneOptions}
          />
        )}

        {error && (
          <Text size="sm" c="red">
            {getErrorMessage(error)}
          </Text>
        )}

        {rows && (
          <Group justify="space-between">
            <Button variant="subtle" onClick={handleGenerate} loading={generate.isPending}>
              {t('competencyMaps.draft.regenerate')}
            </Button>
            <Button onClick={handleSave} disabled={!kept.length} loading={save.isPending}>
              {t('competencyMaps.draft.save', { count: kept.length })}
            </Button>
          </Group>
        )}
      </Stack>
    </Modal>
  );
};
